import { FC, Fragment, PropsWithChildren, ReactElement, ReactNode, useEffect, useMemo, useState } from 'react';
import {
	Cell,
	ColumnDef,
	ExpandedStateList,
	flexRender,
	getCoreRowModel,
	getExpandedRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	Header,
	PaginationState,
	Row,
	RowData,
	RowSelectionOptions,
	RowSelectionState,
	SortingState,
	useReactTable
} from '@tanstack/react-table';
import {
	Box,
	Card,
	CardProps,
	IconButton,
	Stack,
	SvgIconProps,
	Table,
	TableBody,
	TableCell,
	TableCellProps,
	TableContainer,
	TableFooter,
	TableHead,
	TablePagination,
	TablePaginationProps,
	TableProps,
	TableRow,
	Typography,
	TypographyProps
} from '@mui/material';
import { SortOrder } from '@models';
import { SortIcon } from '../SortIcon';
import { DataTableHeaderCheckbox } from './DataTableHeaderCheckbox';
import { DataTableCellCheckbox } from './DataTableCellCheckbox';
import { TablePaginationActions } from './DataTablePaginationActions';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

type DataManipulationModel = 'none' | 'csr' | 'ssr';

declare module '@tanstack/table-core' {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	interface ColumnMeta<TData extends RowData, TValue> {
		hidden?: boolean;
		oneLine?: boolean;
		align?: TableCellProps['align'];
	}
}

const dataFromPath = <DataType extends object = object>(
	dataset: DataType[],
	path: (string | number)[],
	nestedDataKey?: keyof DataType
): DataType | undefined => {
	if (path.length > 1) {
		if (nestedDataKey) {
			return dataFromPath((dataset as any)[path[0]][nestedDataKey], path.slice(1), nestedDataKey);
		}
	} else if (path.length === 1) {
		return (dataset as any)[path[0]];
	}
	return undefined;
};

const pathFromData = <DataType extends object = object>(
	dataset: DataType[],
	searchingData: DataType,
	comparator: (data1: DataType, data2: DataType) => boolean,
	nestedDataKey?: keyof DataType
): number[] => {
	for (let i = 0; i < dataset.length; i++) {
		const currentData = dataset[i];
		if (comparator(currentData, searchingData)) {
			return [i];
		} else if (nestedDataKey && Array.isArray(currentData[nestedDataKey])) {
			const path = pathFromData(currentData[nestedDataKey] as unknown as DataType[], searchingData, comparator, nestedDataKey);
			if (path.length) {
				return [i, ...path];
			}
		}
	}

	return [];
};

export interface IDataTableProps<DataType extends object = object> extends CardProps {
	// core
	columns: ColumnDef<DataType>[];
	data: DataType[];
	comparator: (data1: DataType, data2: DataType) => boolean;
	onRowClick?: (row: Row<DataType>) => void;
	emptyComponent?: ReactElement;

	// styling
	tableProps?: TableProps;
	headerTypographyProps?: TypographyProps;
	headerCellStyle?: (column: Header<DataType, unknown>, columnIndex: number) => TableCellProps['sx'];
	bodyCellStyle?: (cell: Cell<DataType, unknown>, rowIndex: number, columnIndex: number) => TableCellProps['sx'];

	// feature: tree structure
	nestedDataKey?: keyof DataType;
	indentationCol?: keyof DataType;

	// feature: resizable columns
	colsResizable?: boolean;

	// feature: row expand
	expandable?: boolean;
	expandedContent?: (props: { row: Row<DataType> }) => ReactNode;
	isRowExpandable?: (row: Row<DataType>) => boolean;
	expandedRows?: DataType[];
	onExpandedRowsChange?: (selections: DataType[]) => void;
	expandIcon?: FC<SvgIconProps & { row?: Row<DataType> }>;
	collapseIcon?: FC<SvgIconProps & { row?: Row<DataType> }>;

	// feature: row select
	isRowSelectable?: RowSelectionOptions<DataType>['enableRowSelection'];
	selectable?: boolean;
	selections?: DataType[];
	onSelectionsChange?: (selections: DataType[]) => void;

	// feature: pagination
	paginationType?: DataManipulationModel;
	pageIndex?: number;
	pageSize?: number;
	totalCount?: number;
	onPageChange?: (pagination: PaginationState) => void;
	paginationComponentProps?: Partial<TablePaginationProps>;

	// feature: sorting
	sortingType?: DataManipulationModel;
	multiSort?: boolean;
	sorting?: SortingState;
	onSortingChange?: (sorting: SortingState) => void;
}

export const DataTable = <DataType extends object>({
	// core
	columns,
	data,
	comparator,
	onRowClick,
	emptyComponent,

	// styling
	tableProps,
	headerTypographyProps: { sx: headerTypographySx, ...headerTypographyProps } = {},
	headerCellStyle,
	bodyCellStyle,

	// feature: tree structure
	nestedDataKey,
	indentationCol,

	// feature: resizable columns
	colsResizable,

	// feature: row expand
	expandable,
	expandedContent,
	isRowExpandable,
	expandedRows = [],
	onExpandedRowsChange,
	expandIcon: ExpandIcon = ExpandMoreIcon,
	collapseIcon: CollapseIcon = ExpandLessIcon,

	// feature: row select
	isRowSelectable,
	selectable,
	selections = [],
	onSelectionsChange,

	// feature: pagination
	paginationType = 'none',
	pageIndex = 0,
	pageSize = 10,
	totalCount = data.length,
	onPageChange,
	paginationComponentProps,

	// feature: sorting
	sortingType = 'none',
	multiSort = false,
	sorting = [],
	onSortingChange,

	// rest
	sx: containerSx,
	...containerProps
}: PropsWithChildren<IDataTableProps<DataType>>) => {
	const rowSelection = useMemo(
		() =>
			selections.reduce<RowSelectionState>((acc, current) => {
				const path = pathFromData(data, current, comparator, nestedDataKey);
				return path.length ? { ...acc, [path.join('.')]: true } : acc;
			}, {}),
		[data, selections, comparator, nestedDataKey]
	);

	const expanded = useMemo(
		() =>
			expandedRows.reduce<ExpandedStateList>((acc, current) => {
				const path = pathFromData(data, current, comparator, nestedDataKey);
				return path.length ? { ...acc, [path.join('.')]: true } : acc;
			}, {}),
		[data, expandedRows, comparator, nestedDataKey]
	);

	const cols = useMemo<ColumnDef<DataType>[]>(() => {
		const retValue = [...columns];
		if (selectable) {
			retValue.unshift({
				id: 'prefixCell',
				enableSorting: false,
				size: 0.01,
				header: ({ table }) => (selectable ? <DataTableHeaderCheckbox table={table} /> : null),
				cell: ({ row }) =>
					selectable ? (
						<Stack direction="row" alignItems="center">
							<DataTableCellCheckbox row={row} />
						</Stack>
					) : null
			});
		}
		if (expandable || nestedDataKey) {
			retValue.unshift({
				id: 'suffixCell',
				enableSorting: false,
				size: 0.01,
				header: () => null,
				cell: ({ row }) => (
					<Box sx={{ width: 40, height: 40 }}>
						<Stack direction="row" alignItems="center">
							{row.getCanExpand() && (nestedDataKey ? !!(row.original[nestedDataKey] as any)?.length : true) ? (
								<IconButton size="medium" onClick={row.getToggleExpandedHandler()}>
									{row.getIsExpanded() ? (
										<CollapseIcon fontSize="small" color="inherit" row={row} />
									) : (
										<ExpandIcon fontSize="small" color="inherit" row={row} />
									)}
								</IconButton>
							) : null}
						</Stack>
					</Box>
				)
			});
		}
		return retValue;
	}, [columns, expandable, selectable, nestedDataKey]);

	const [currentPagination, setCurrentPagination] = useState<PaginationState>({ pageIndex, pageSize });
	const [currentSorting, setCurrentSorting] = useState<SortingState>(sortingType === 'none' ? [] : sorting.map((s) => ({ ...s })));

	useEffect(() => {
		if (sortingType === 'none') {
			const newSorting: SortingState = [];
			JSON.stringify(currentSorting) !== JSON.stringify(newSorting) && setCurrentSorting(newSorting);
		} else if (sortingType === 'ssr') {
			const newSorting: SortingState = sorting.map((s) => ({ ...s }));
			JSON.stringify(currentSorting) !== JSON.stringify(newSorting) && setCurrentSorting(newSorting);
		}
	}, [sortingType, sorting, currentSorting, setCurrentSorting]);

	useEffect(() => {
		(paginationType === 'none' || paginationType === 'ssr') && setCurrentPagination({ pageIndex, pageSize });
	}, [paginationType, pageIndex, pageSize, setCurrentPagination]);

	const tableInstance = useReactTable({
		columns: cols,
		data,
		state: { rowSelection, expanded, pagination: currentPagination, sorting: currentSorting },

		// COMMON configs
		getCoreRowModel: getCoreRowModel(),

		// TREE STRUCTURE config
		getSubRows: (row) => (nestedDataKey ? (row[nestedDataKey] as unknown as DataType[]) : undefined),

		// SELECTION configs
		enableRowSelection: isRowSelectable ?? true,
		onRowSelectionChange: (val) => {
			const newState = typeof val === 'function' ? val(rowSelection) : val;
			const selectedData = Object.keys(newState).map((index) => dataFromPath(data, index.split('.'), nestedDataKey));
			onSelectionsChange?.(selectedData.filter(Boolean) as DataType[]);
		},

		// EXPANDING configs
		getRowCanExpand: isRowExpandable,
		getExpandedRowModel: getExpandedRowModel(),
		onExpandedChange: (val) => {
			const newState = typeof val === 'function' ? val(expanded) : val;
			const expandedData = Object.keys(newState).map((index) => dataFromPath(data, index.split('.'), nestedDataKey));
			onExpandedRowsChange?.(expandedData.filter(Boolean) as DataType[]);
		},

		// RESIZE configs
		columnResizeMode: colsResizable ? 'onChange' : undefined,

		// PAGINATION configs
		getPaginationRowModel: paginationType === 'csr' ? getPaginationRowModel() : undefined,
		manualPagination: paginationType === 'ssr',
		onPaginationChange: (newPagination) => {
			const newState =
				typeof newPagination === 'function' ? newPagination(currentPagination || { pageIndex, pageSize }) : newPagination;
			onPageChange?.(newState);
			paginationType === 'csr' && setCurrentPagination(newState);
		},

		// SORTING configs
		getSortedRowModel: sortingType === 'csr' ? getSortedRowModel() : undefined,
		enableSorting: sortingType !== 'none',
		enableMultiSort: multiSort,
		manualSorting: sortingType === 'ssr',
		onSortingChange: (newSorting) => {
			const newState = typeof newSorting === 'function' ? newSorting(currentSorting) : newSorting;
			onSortingChange?.(newState);
			sortingType === 'csr' && setCurrentSorting(newState);
		}
	});

	useEffect(() => {
		nestedDataKey && tableInstance.toggleAllRowsExpanded(true);
	}, [tableInstance, nestedDataKey]);

	return data.length || !emptyComponent ? (
		<Card
			variant="outlined"
			sx={{
				display: 'flex',
				flexDirection: 'column',
				maxHeight: 1.0,
				overflow: 'hidden',
				width: 1.0,
				boxSizing: 'border-box',
				...containerSx
			}}
			{...containerProps}
		>
			<TableContainer sx={{ maxHeight: 1.0 }}>
				<Table {...tableProps}>
					<TableHead>
						{tableInstance.getHeaderGroups().map((headerGroup) => (
							<TableRow key={`header-${headerGroup.id}`}>
								{headerGroup.headers.map((header, j) => {
									return !header.column.columnDef.meta?.hidden ? (
										<TableCell
											key={`header-cell-${headerGroup.id}-${header.id}`}
											align={header.column.columnDef.meta?.align}
											colSpan={header.colSpan}
											sx={{
												paddingX: 2,
												paddingY: 1.5,
												backgroundColor: 'common.black',
												color: 'common.white',
												boxSizing: 'border-box',
												position: 'relative',
												width: header.column.columnDef.size || 'auto',
												maxWidth: header.column.columnDef.size || 'auto',
												...headerCellStyle?.(header, j),
												...(header.column.getIsResizing() ? { background: 'rgba(0, 0, 0, 0.05)' } : {})
											}}
										>
											<Stack
												direction="row"
												alignItems="center"
												spacing={1}
												sx={{
													minHeight: 32,
													whiteSpace: header.column.columnDef.meta?.oneLine ? 'nowrap' : undefined
												}}
											>
												<Typography
													variant="body1"
													sx={{ ...headerTypographySx, wordBreak: 'keep-all' }}
													{...headerTypographyProps}
												>
													{header.isPlaceholder
														? null
														: flexRender(header.column.columnDef.header, header.getContext())}
												</Typography>
												<SortIcon
													sortable={header.column.getCanSort()}
													onClick={() =>
														header.column.getCanSort() && header.column.toggleSorting(undefined, true)
													}
													sortOrder={
														header.column.getIsSorted()
															? header.column.getIsSorted() === 'desc'
																? SortOrder.DESC
																: SortOrder.ASC
															: undefined
													}
												/>
											</Stack>
											{colsResizable ? (
												<Box
													onMouseDown={header.getResizeHandler()}
													onTouchStart={header.getResizeHandler()}
													onClick={(e) => e.stopPropagation()}
													sx={{
														position: 'absolute',
														right: 0,
														transform: 'translateX(-50%)',
														top: 0,
														height: '100%',
														width: '8px',
														cursor: 'col-resize',
														userSelect: 'none',
														touchAction: 'none'
													}}
												/>
											) : null}
										</TableCell>
									) : null;
								})}
							</TableRow>
						))}
					</TableHead>
					<TableBody>
						{tableInstance.getRowModel().rows.map((row, rowIndex) => {
							const isRowClickable =
								onRowClick &&
								((expandable && isRowExpandable?.(row)) ||
									(nestedDataKey && (row.original[nestedDataKey] as unknown as DataType[])?.length));
							return (
								<Fragment key={`row-${row.id}`}>
									<TableRow
										onClick={() => onRowClick?.(row)}
										sx={{
											cursor: isRowClickable ? 'pointer' : undefined
										}}
										selected={row.getCanSelect() && row.getIsSelected()}
									>
										{row.getVisibleCells().map((cell, cellIndex) => {
											return !cell.column.columnDef.meta?.hidden ? (
												<TableCell
													key={`row-${row.id}-cell-${cell.id}`}
													align={cell.column.columnDef.meta?.align}
													sx={{
														paddingX: 2,
														minHeight: 80,
														height: 80,
														backgroundColor: rowIndex % 2 ? 'grey.50' : 'common.white',
														borderLeft: (theme) =>
															cellIndex === 0 ? undefined : `1px solid ${theme.palette.grey[100]}`,
														...bodyCellStyle?.(cell, rowIndex, cellIndex)
													}}
												>
													<Stack
														direction="row"
														sx={{
															alignItems: 'center',
															flexWrap: 'wrap',
															whiteSpace: cell.column.columnDef.meta?.oneLine ? 'nowrap' : undefined
														}}
													>
														{cell.column.id === indentationCol ? (
															<Stack direction="row" sx={{ alignItems: 'center' }}>
																{Array.from(new Array(row.depth)).map((_, i, { length }) => (
																	<Box
																		key={`indentation-${cell.column.id}-${i}`}
																		sx={{
																			width: 24,
																			height: 'inherit',
																			minHeight: 'inherit',
																			borderLeft: (theme) =>
																				i === length - 1
																					? `1px dashed ${theme.palette.grey[400]}`
																					: undefined,
																			marginLeft: 1.5
																		}}
																	></Box>
																))}
															</Stack>
														) : null}
														{flexRender(cell.column.columnDef.cell, cell.getContext())}
													</Stack>
												</TableCell>
											) : null;
										})}
									</TableRow>
									{row.getIsExpanded() && expandedContent ? (
										<TableRow>
											<TableCell colSpan={row.getVisibleCells().length} sx={{ padding: 0 }}>
												{expandedContent({ row })}
											</TableCell>
										</TableRow>
									) : null}
								</Fragment>
							);
						})}
					</TableBody>
				</Table>
			</TableContainer>

			{paginationType !== 'none' ? (
				<TableFooter component="div">
					<TablePagination
						component="div"
						rowsPerPageOptions={[5, 10, 25]}
						colSpan={cols.length}
						ActionsComponent={TablePaginationActions}
						{...paginationComponentProps}
						count={paginationType === 'ssr' ? totalCount : tableInstance.getFilteredRowModel().rows.length}
						rowsPerPage={currentPagination.pageSize}
						page={currentPagination.pageIndex}
						onPageChange={(_, page) => {
							tableInstance.setPageIndex(page);
						}}
						onRowsPerPageChange={(e) => {
							const size = e.target.value ? Number(e.target.value) : 10;
							tableInstance.setPageSize(size);
						}}
					/>
				</TableFooter>
			) : null}
		</Card>
	) : (
		emptyComponent
	);
};
